// Adapt a MerchandisingProduct (returned by GET /retailers/{id}/products)
// to the shape the product list UI (ProductsTable / ProductRow) expects.
// Fields not carried by the document are filled with safe placeholders
// ("N/A" / null / false).

// The document carries every translation as [{locale, translated}]. getTranslation()
// expects the REST shape ({locale: {name}}) to resolve user locale -> organization
// default locale: a plain array would only ever hit its last-resort branch and show
// whichever locale Elasticsearch happened to store first.
const toTranslations = (translated) =>
    Object.fromEntries((translated || []).map((t) => [t.locale, {locale: t.locale, name: t.translated}]));

// Shared frozen constant: the row is rebuilt on every fetch, and a fresh [] would give
// every memoized consumer a new identity for "no error".
const EMPTY_VALIDATION_ERRORS = Object.freeze([]);

export function mapMerchandisingProductToProduct(esProduct) {
    return {
        // Mass actions (massUpdate) collect the selected rows through item["@id"].
        "@id": `/products/${esProduct.id}`,
        id: esProduct.id,
        // owner retailer — the merchandising row (cross-retailer) builds the
        // /retailers/show/{retailerId}/products/show/{id} link from it
        retailerId: esProduct.retailerId ?? null,
        ranking: esProduct.ranking ?? null,
        rankings: esProduct.rankings ?? [],
        // buyer demand tier (0 = no data), see MerchandisingProduct::groupSearchImpressions
        groupedSearchImpression: esProduct.groupedSearchImpression ?? 0,
        // all-time sales counters (docs/variation-sales.md); null until the
        // document carries the fields (pre-reindex) -> N/A on the list
        onlineSales: esProduct.onlineSales ?? null,
        storeSales: esProduct.storeSales ?? null,
        translations: toTranslations(esProduct.productName),
        sku: esProduct.sku ?? "N/A",
        externalId: "N/A", // not indexed yet
        eshopId: esProduct.eshopId ?? null,
        pictures: esProduct.pictureUrl ? [esProduct.pictureUrl] : [],
        brand: {name: esProduct.brand ?? "N/A"},
        eikow: esProduct.eikow ?? false,
        variationFromFeed: esProduct.variationFromFeed ?? false,
        // directCategories = the product's real categories (the `categories`
        // field also carries every ancestor for merchandising navigation, which
        // is NOT what the list should display or mass actions operate on).
        categories: (esProduct.directCategories || []).map((category) => ({
            id: category.id,
            translations: toTranslations(category.name),
            draft: category.draft ?? false,
        })),
        updatedAt: esProduct.updatedAt ?? null,
        // Last edit made by a human, and its author (null when the product has only
        // ever been written by imports). Same names as on the entity so the list reads
        // them identically whatever the source of the row.
        manualUpdatedAt: esProduct.manualUpdatedAt ?? null,
        manualUpdatedBy: esProduct.manualUpdatedBy ?? null,
        // Product url on the eshop, resolved in the organization's locale.
        url: esProduct.url ?? null,
        // One entry per variation: option-composed display name + global stock.
        variations: (esProduct.variations || []).map((variation) => ({
            id: variation.id,
            name: variation.name,
            stock: variation.stock ?? 0,
        })),
        lowestPrice: esProduct.lowestPrice ?? null,
        highestPrice: esProduct.highestPrice ?? null,
        // signed % vs the market benchmark (null = no scoring data)
        priceCompetitiveness: esProduct.priceCompetitiveness ?? null,
        // ProductRow reads product.currency.code (then ignores it — the real
        // currency comes from the organization). Provide it so the read does
        // not crash; the document does not carry a currency.
        currency: {code: null},
        status: esProduct.status ?? false,
        validated: esProduct.validated ?? false,
        // Share of passing validation checks /100. Same name as on the entity, so
        // the list reads it identically whether the row came from this document or
        // from the plain /products collection. Null until the product is updated
        // after the field was introduced -> the completeness pill stays hidden.
        validationScore: esProduct.validationScore ?? null,
        // e-commerce quality score, computed by the scoring cron. Null when the
        // document does not carry the field -> the score badge and its drawer
        // stay hidden.
        score: esProduct.score ?? null,
        // full scoring breakdown (ScoringBreakdown::toArray(): per-bloc points
        // and maxima), displayed in the drawer opened from the score badge
        scoring: esProduct.scoring ?? null,
        // createdAt of the selling-properties snapshot the score was computed
        // from (RFC3339) — the drawer's "computed on" line
        scoringDate: esProduct.scoringDate ?? null,
        ctr: esProduct.ctr ?? null,
        // {path, message} pairs explaining why validated is false — the KO tag's tooltip.
        // Legitimately empty on an invalid product: the API's verdict is not "zero
        // violations" (a product with none of its own is still invalid when no variation
        // passes), in which case the tag carries no tooltip.
        validationErrors: esProduct.validationErrors ?? EMPTY_VALIDATION_ERRORS,
        hasPushError: esProduct.hasPushError ?? false,
        errorOnPush: false, // not indexed yet
        lastErrorDate: null, // not indexed yet
    };
}
